import { useRouter } from "next/router"
import React, { useState } from "react"
import {
  Button,
  Card,
  FormField,
  Heading,
  Link,
  Loader,
  Notice,
  Table,
  Text,
  TextField,
} from "@smooth/design-system-components"
import { describeApiError, formatText } from "src/features/admin/screen-shared"
import { useGetUserBankAccounts } from "../hooks/useGetUserBankAccounts"
import {
  userBankAccountHref,
  userHref,
  type UserBankAccountListItem,
  type UserBankAccountListQuery,
} from "../types"

// 列の表記は SmootherOPE と同じ（Bank Code の列には銀行名を出す）
const COLUMNS = [
  { key: "id", header: "Id" },
  { key: "user", header: "User" },
  { key: "bankCode", header: "Bank Code" },
  { key: "branchCode", header: "Branch Code" },
]

const toRow = (b: UserBankAccountListItem): Record<string, React.ReactNode> => ({
  id: <Link href={userBankAccountHref(b.id)}>{b.id}</Link>,
  user: b.userId != null ? <Link href={userHref(b.userId)}>{formatText(b.userName)}</Link> : "—",
  // 銀行名が引けないときは空欄（SmootherOPE と同じ）
  bankCode: b.bankName ?? "",
  branchCode: formatText(b.branchCode),
})

export const UserBankAccountList: React.FC = () => {
  const router = useRouter()
  const [searchInput, setSearchInput] = useState("")
  const [query, setQuery] = useState<UserBankAccountListQuery>({ search: "", page: 1 })

  const { bankAccounts, totalCount, perPage, isLoading, error } = useGetUserBankAccounts(query)
  const totalPages = Math.max(1, Math.ceil(totalCount / perPage))

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setQuery({ search: searchInput.trim(), page: 1 })
  }

  return (
    <div className="flex flex-col gap-6">
      <Heading level={1} size="lg">
        Bank Accounts
      </Heading>

      <Card variant="outlined">
        <form onSubmit={handleSearch} className="flex flex-wrap items-end gap-3">
          <FormField label="検索（ユーザー ID）">
            <TextField
              value={searchInput}
              placeholder="例: 12345"
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </FormField>
          <Button type="submit" variant="outline">
            検索
          </Button>
        </form>
      </Card>

      <Text size="sm" tone="muted">
        {totalCount.toLocaleString()} 件
      </Text>

      {error ? (
        <Notice tone="error" variant="card">
          {describeApiError(error, "一覧を読み込めませんでした")}
        </Notice>
      ) : isLoading ? (
        <Loader />
      ) : bankAccounts.length === 0 ? (
        <Notice variant="card">該当する口座はありません</Notice>
      ) : (
        <div className="overflow-x-auto">
          <Table
            columns={COLUMNS}
            rows={bankAccounts.map(toRow)}
            onRowClick={(index) => router.push(userBankAccountHref(bankAccounts[index].id))}
          />
        </div>
      )}

      <div className="flex items-center justify-center gap-3">
        <Button
          type="button"
          variant="outline"
          disabled={query.page <= 1}
          onClick={() => setQuery((q) => ({ ...q, page: q.page - 1 }))}
        >
          前へ
        </Button>
        <Text size="sm">
          {query.page} / {totalPages} ページ
        </Text>
        <Button
          type="button"
          variant="outline"
          disabled={query.page >= totalPages}
          onClick={() => setQuery((q) => ({ ...q, page: q.page + 1 }))}
        >
          次へ
        </Button>
      </div>
    </div>
  )
}
